import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { isEmpty } from 'lodash'
import { useTheme } from '@emotion/react'
import {
  Badge,
  Inline,
  Container,
  Section,
  Row,
  RowColumn,
  ListItem,
} from 'chaoskit/src/components'
import { misc, link, text } from 'chaoskit/src/assets/styles/utility'

import { HeroContent } from '../Hero'
import HelmetSEO from '../global/HelmetSEO'
import ArticleFooter from '../global/ArticleFooter'

import ChaosMonkeyHero from './Hero'

import { getArticleType } from '~utils/helpers'
import TextLink from '~components/TextLink'
import Data from '~components/Data'
import TableOfContents from '~components/TableOfContents'
import Icon from '~components/Icon'
import { micrositeContentOverrides } from '~styles/utility'
import ChaosMonkeyLayout from '~layouts/ChaosMonkeyLayout'
import NodeTypeTracker from '~partials/global/NodeTypeTracker'

const StylesAlerts = (theme) => ({
  '.G__ChaosMonkey__ArticleCallout': {
    position: 'relative',
    zIndex: 2,
    paddingTop: theme.space.large,
    paddingBottom: theme.space.large,

    '&::before': {
      content: "''",
      background: theme.color.primary.base,
      position: 'absolute',
      width: '100vw',
      top: 0,
      height: '100%',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: '-1',
    },

    a: [text.underline],

    '.custom-block-heading': [
      text.heading(theme),
      theme.fontSize.h3__fluid,
      {
        marginBottom: theme.space.base,
      },
    ],
  },
})

const Article = ({
  alternatives,
  data: {
    article: {
      frontmatter: {
        title,
        date,
        seoTitle,
        titleSub,
        description,
        hideTableOfContents,
        heroImage,
        socialMediaImage,
        hideReadingTime,
      },
      fields: { heroContrast },
      headings,
      tableOfContents,
      body,
      timeToRead,
      fileAbsolutePath,
    },
  },
  pageContext: { nodeType, previous, next },
}) => {
  const theme = useTheme()

  const renderTableOfContents = !hideTableOfContents && headings.length

  // If we don't have a hero background set; fallback to alternative
  const heroSrc =
    heroImage ||
    'https://res.cloudinary.com/gremlin/image/upload/t_default/ChaosMonkey-Alternatives-Hero.svg'

  return (
    <Fragment>
      <HelmetSEO
        title={seoTitle || title}
        description={description}
        image={
          socialMediaImage ||
          'https://res.cloudinary.com/gremlin/image/upload/t_default/ChaosMonkeyAlternatives.png'
        }
        type="article"
      />
      <NodeTypeTracker nodeType={nodeType} />
      <ChaosMonkeyHero
        css={{ alignItems: 'flex-start' }}
        background={heroSrc}
        contrast={heroContrast}
      >
        <HeroContent>
          <Container>
            <Inline css={{ fontSize: theme.fontSize.small }}>
              <ListItem>
                <TextLink
                  css={[
                    !heroContrast && [link.reset(theme), link.linkUnderline],
                  ]}
                  className={heroContrast && 'u-link'}
                  href="/chaos-monkey/"
                >
                  Chaos Monkey
                </TextLink>
              </ListItem>
              {alternatives && (
                <Fragment>
                  <ListItem>
                    <Icon
                      size="small"
                      css={{
                        color: heroContrast
                          ? theme.contrast.muted
                          : theme.fontColor.muted,
                      }}
                      icon="caret-right"
                    />
                  </ListItem>
                  <ListItem>
                    <TextLink
                      css={[
                        !heroContrast && [
                          link.reset(theme),
                          link.linkUnderline,
                        ],
                      ]}
                      className={heroContrast && 'u-link'}
                      href="/chaos-monkey/chaos-monkey-alternatives/"
                    >
                      Chaos Monkey Alternatives
                    </TextLink>
                  </ListItem>
                </Fragment>
              )}
              <ListItem>
                <Icon
                  size="small"
                  css={{
                    color: heroContrast
                      ? theme.contrast.muted
                      : theme.fontColor.muted,
                  }}
                  icon="caret-right"
                />
              </ListItem>
              <ListItem>
                <span>{title}</span>
              </ListItem>
            </Inline>
          </Container>
        </HeroContent>
      </ChaosMonkeyHero>
      <main css={{ background: theme.color.primary.light }}>
        <Section>
          <Container>
            <article
              css={{
                background: theme.color.light.base,
                boxShadow: theme.boxShadow.large,
                padding: theme.space.large,
                marginTop: -theme.space.xlarge + -150,

                [theme.mq.medium]: {
                  padding: theme.space.xlarge,
                },
              }}
            >
              <Row gutter={{ base: 'large' }}>
                {renderTableOfContents ? (
                  <RowColumn
                    css={{
                      [theme.mq.small__max]: {
                        display: 'none',
                      },
                    }}
                    size={{ base: 3 }}
                  >
                    <TableOfContents tree={tableOfContents} />
                  </RowColumn>
                ) : null}
                <RowColumn size={{ medium: renderTableOfContents ? 9 : null }}>
                  <div
                    css={[
                      misc.fluidSize({
                        theme,
                        from: theme.space.large,
                        to: theme.space.xlarge,
                        property: 'marginBottom',
                      }),
                      !renderTableOfContents && {
                        textAlign: 'center',
                      },
                    ]}
                  >
                    <h5
                      css={[
                        theme.fontSize.medium__fluid,
                        {
                          color: theme.color.primary.base,
                        },
                      ]}
                    >
                      {alternatives ? 'Chaos Monkey Alternatives' : title}
                    </h5>
                    <h1
                      css={[
                        theme.fontSize.h2__fluid,
                        {
                          marginTop: 0,
                          marginBottom: theme.space.large,
                        },
                      ]}
                    >
                      {alternatives ? title : titleSub}
                    </h1>
                    <Inline
                      css={[
                        !renderTableOfContents && {
                          justifyContent: 'center',
                        },
                      ]}
                    >
                      {!hideReadingTime && (
                        <ListItem>
                          <Badge type="primary">{timeToRead} min read</Badge>
                        </ListItem>
                      )}
                      <ListItem>
                        <div
                          css={{
                            fontSize: theme.fontSize.small,
                            color: theme.fontColor.muted,
                          }}
                        >
                          Last Updated {date}
                        </div>
                      </ListItem>
                    </Inline>
                  </div>
                  <div
                    css={[
                      micrositeContentOverrides(theme),
                      StylesAlerts(theme),
                    ]}
                  >
                    <Data markdown>{body}</Data>
                  </div>
                </RowColumn>
              </Row>
              <ArticleFooter
                navigation={{
                  previous: isEmpty(previous) ? null : previous,
                  next: isEmpty(next) ? null : next,
                }}
                homeLabel="Chaos Monkey"
                homeUrl={
                  getArticleType({ fileAbsolutePath }) === 'chaos-monkey'
                    ? '/chaos-monkey/'
                    : '/chaos-monkey/chaos-monkey-alternatives/'
                }
              />
            </article>
          </Container>
        </Section>
      </main>
    </Fragment>
  )
}

Article.propTypes = {
  alternatives: PropTypes.bool,
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
}

const ArticleContent = (props) => {
  const {
    data: {
      article: {
        fields: { heroContrast },
      },
    },
  } = props

  return (
    <ChaosMonkeyLayout contrast={heroContrast}>
      <Article {...props} />
    </ChaosMonkeyLayout>
  )
}

ArticleContent.propTypes = {
  data: PropTypes.object.isRequired,
}

export default ArticleContent
